import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { compose, withHandlers, withState } from 'recompose'
import { debounce, values } from 'lodash'

import { Input } from 'components/input/Input'

import './SearchInput.scss'

const SEARCH_INPUT_TYPES = {
  DEFAULT: 'DEFAULT',
  HEADER_SEARCH: 'HEADER_SEARCH',
}

const DEFAULT_DEBOUNCE_TIMEOUT = 300

const SearchInputCmp = ({
  value,
  isAutoFocus = false,
  handleChange,
  handleClear,
  disabled,
  placeholder = 'Search...',
  size = 'regular',
  type = SEARCH_INPUT_TYPES.DEFAULT,
  className,
  mask,
  dataTestId,
  onFocus,
  onBlur,
  shouldBeClearable,
  isInputLight,
}) => {
  const inputRef = useRef(null)

  useEffect(() => {
    if (isAutoFocus) {
      setTimeout(() => {
        inputRef.current.focus()
      })
    }
  }, [inputRef, isAutoFocus])

  return (
    <Input
      type="text"
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      className={classNames('search-input-cmp', className, {
        'search-input-cmp__header-search': type === SEARCH_INPUT_TYPES.HEADER_SEARCH,
      })}
      size={size}
      disabled={disabled}
      onClear={handleClear}
      icon="search"
      hasClearIcon
      mask={mask}
      testId={dataTestId}
      onFocus={onFocus}
      onBlur={onBlur}
      setInputRef={inputRef}
      shouldBeClearable={shouldBeClearable}
      isInputLight={isInputLight}
    />
  )
}

SearchInputCmp.propTypes = {
  value: PropTypes.any.isRequired,
  isAutoFocus: PropTypes.bool,
  handleChange: PropTypes.func.isRequired,
  handleClear: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['regular', 'small']),
  type: PropTypes.oneOf(values(SEARCH_INPUT_TYPES)),
  className: PropTypes.string,
  mask: PropTypes.node,
  dataTestId: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  shouldBeClearable: PropTypes.bool,
  isInputLight: PropTypes.bool,
}

const handleChange =
  ({ searchDebounce, onChange, limit }) =>
  value => {
    searchDebounce.cancel()

    if (value.trim().length >= limit) {
      searchDebounce()
    }

    onChange(value)
  }

const handleClear =
  ({ onChange, onClear }) =>
  () => {
    onChange('')
    onClear && onClear()
  }

export const SearchInput = compose(
  withState('searchDebounce', 'setSearchDebounce', ({ onSearch, debounceTimeout = DEFAULT_DEBOUNCE_TIMEOUT }) =>
    debounce(onSearch, debounceTimeout, { trailing: true }),
  ),
  withHandlers({
    handleChange,
    handleClear,
  }),
)(SearchInputCmp)

SearchInput.propTypes = {
  value: PropTypes.string,
  isAutoFocus: PropTypes.bool,
  onSearch: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  limit: PropTypes.number,
  size: PropTypes.oneOf(['regular', 'small']),
  type: PropTypes.oneOf(values(SEARCH_INPUT_TYPES)),
  debounceTimeout: PropTypes.number,
  shouldBeCleared: PropTypes.bool,
  className: PropTypes.string,
  dataTestId: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  setInputRef: PropTypes.func,
  shouldBeClearable: PropTypes.bool,
  isInputLight: PropTypes.bool,
}
